import React from 'react';
import { twMerge } from 'tailwind-merge';

import { isAPIError } from 'types/api_error';
import { OrganizationUsageLimits } from 'types/organization_usage_limits';

import { NUMBER_FORMAT } from 'services/number';
import { useUpdateWorkspaceUsageLimits } from 'organizations/hooks/useUpdateWorkspaceUsageLimits';
import { WorkspaceUtilization } from 'organizations/hooks/useWorkspacesUtilization';
import { formatUtilizationLimit } from 'organizations/utils/utilizationLimit';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import {
  getBytes,
  getDisplayValue,
} from 'components/FileSizeInput/convert_file_size';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { NumberInput } from 'components/NumberInput/NumberInput';
import { Text } from 'components/Text/Text';
import * as Dialog from 'components/Dialog';

export type WorkspaceLimitKey = keyof OrganizationUsageLimits;

export type WorkspaceAllocatableLimit = {
  key: WorkspaceLimitKey;
  /** Lower-case resource name, e.g. "live streams". */
  name: string;
  unit?: 'filesize' | 'minutes';
  limitUnit: string;
  /** Effective organization-wide limit (org override if set, else plan). */
  organizationLimit: number | null;
  /**
   * Retention limits are inherited caps, not a shared pool, and get
   * different summary copy.
   */
  isRetention?: boolean;
};

type RowState = {
  capped: boolean;
  /** Value in display units (GiB for filesize limits). */
  value: number | '';
  initialCapped: boolean;
  initialValue: number | '';
};

export type WorkspaceLimitAllocationDialogProps = {
  /** The limit being allocated. Pass `null` to keep the dialog closed. */
  limit: WorkspaceAllocatableLimit | null;
  workspaces: WorkspaceUtilization[];
  onClose: () => void;
};

/**
 * API value → display units (GiB for filesize limits). Rounds down to 1
 * decimal so the displayed cap (and the org-limit default it seeds) never
 * converts back to more bytes than the source limit — otherwise seeding a
 * cap from the org limit would exceed it and fail the API's validation.
 */
function toDisplayValue(limit: WorkspaceAllocatableLimit, value: number) {
  if (limit.unit === 'filesize') {
    return Math.floor(Number(getDisplayValue(value, 'GiB')) * 10) / 10;
  }

  return value;
}

/** Display units → API value (bytes for filesize limits). */
function toApiValue(limit: WorkspaceAllocatableLimit, value: number) {
  if (limit.unit === 'filesize') {
    return Math.round(Number(getBytes(value, 'GiB')));
  }

  return value;
}

function formatDisplayValue(limit: WorkspaceAllocatableLimit, value: number) {
  return formatUtilizationLimit(
    toApiValue(limit, value),
    limit.unit,
    limit.limitUnit
  );
}

function getSaveErrorMessage(error: unknown) {
  if (isAPIError(error)) {
    const contextDetails = error.context?.details;

    if (typeof contextDetails === 'string') {
      return contextDetails;
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unable to update workspace utilization limits.';
}

export function WorkspaceLimitAllocationDialog({
  limit,
  workspaces,
  onClose,
}: WorkspaceLimitAllocationDialogProps) {
  if (!limit) {
    return null;
  }

  return (
    <AllocationDialogContent
      key={limit.key}
      limit={limit}
      workspaces={workspaces}
      onClose={onClose}
    />
  );
}

function AllocationDialogContent({
  limit,
  workspaces,
  onClose,
}: WorkspaceLimitAllocationDialogProps & {
  limit: WorkspaceAllocatableLimit;
}) {
  const [rows, setRows] = React.useState<Record<string, RowState>>(() =>
    Object.fromEntries(
      workspaces
        // Rows are only editable once their current limits have loaded.
        .filter((workspace) => workspace.limits !== undefined)
        .map((workspace) => {
          const currentLimit = workspace.limits?.[limit.key];
          const capped = Number.isFinite(currentLimit);
          const value = capped
            ? toDisplayValue(limit, Number(currentLimit))
            : '';

          return [
            workspace.application.id,
            { capped, value, initialCapped: capped, initialValue: value },
          ];
        })
    )
  );

  // Workspaces that load after the dialog opened are intentionally excluded;
  // row state is captured when the dialog mounts.
  const editableWorkspaces = workspaces.filter(
    (workspace) => rows[workspace.application.id] !== undefined
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const { mutateAsync: updateWorkspaceUsageLimits } =
    useUpdateWorkspaceUsageLimits();

  const organizationDisplayLimit = Number.isFinite(limit.organizationLimit)
    ? toDisplayValue(limit, Number(limit.organizationLimit))
    : null;
  const organizationLimitLabel = formatUtilizationLimit(
    limit.organizationLimit,
    limit.unit,
    limit.limitUnit
  );

  // Stable callbacks so memoized rows only re-render when their own state
  // changes (a slider drag fires many events).
  const setRowValue = React.useCallback(
    (applicationId: string, value: number | '') => {
      setRows((rows) => ({
        ...rows,
        [applicationId]: { ...rows[applicationId], value },
      }));
    },
    []
  );

  const setRowMode = React.useCallback(
    (applicationId: string, capped: boolean) => {
      setRows((rows) => {
        const row = rows[applicationId];
        let { value } = row;

        if (capped && value === '') {
          // Sensible starting point when capping for the first time.
          value = organizationDisplayLimit ?? 0;
        }

        return { ...rows, [applicationId]: { ...row, capped, value } };
      });
    },
    [organizationDisplayLimit]
  );

  const rowStates = editableWorkspaces.map((workspace) => {
    const row = rows[workspace.application.id];
    const exceedsOrganizationLimit = Boolean(
      row.capped &&
        row.value !== '' &&
        organizationDisplayLimit !== null &&
        Number(row.value) > organizationDisplayLimit
    );

    return {
      workspace,
      row,
      exceedsOrganizationLimit,
      isIncomplete: row.capped && row.value === '',
    };
  });

  const hasError = rowStates.some((state) => state.exceedsOrganizationLimit);
  const isIncomplete = rowStates.some((state) => state.isIncomplete);

  const cappedRows = rowStates.filter(
    (state) => state.row.capped && state.row.value !== ''
  );
  const uncappedCount = rowStates.length - cappedRows.length;
  const committed = cappedRows.reduce(
    (total, state) => total + Number(state.row.value),
    0
  );
  const isOvercommitted = Boolean(
    !limit.isRetention &&
      organizationDisplayLimit !== null &&
      committed > organizationDisplayLimit
  );

  const cappedCountLabel = `${cappedRows.length} workspace${
    cappedRows.length === 1 ? '' : 's'
  }`;
  const uncappedCountLabel = `${uncappedCount} workspace${
    uncappedCount === 1 ? '' : 's'
  }`;
  // Bare amount for the committed total; the org limit label carries the unit.
  const committedLabel =
    limit.unit === 'filesize'
      ? formatDisplayValue(limit, committed)
      : NUMBER_FORMAT.format(committed);

  let summary: string;
  if (limit.isRetention) {
    summary =
      organizationDisplayLimit !== null
        ? `${cappedCountLabel} capped below the org max · ${uncappedCount} inherit ${organizationLimitLabel}.`
        : `${cappedCountLabel} capped · ${uncappedCount} follow the org default.`;
  } else if (organizationDisplayLimit === null) {
    summary = `${cappedCountLabel} with a fixed cap · ${uncappedCount} uncapped (share the org limit).`;
  } else if (isOvercommitted) {
    summary = `Committed caps (${formatDisplayValue(
      limit,
      committed
    )}) exceed the org limit of ${organizationLimitLabel} — reduce a cap or raise the org limit.`;
  } else {
    summary = `${committedLabel} of ${organizationLimitLabel} committed to caps · ${uncappedCountLabel} uncapped (share the org limit).`;
  }

  async function handleSave() {
    setSaveError(null);
    setIsSaving(true);

    try {
      const updates = editableWorkspaces.filter((workspace) => {
        const row = rows[workspace.application.id];

        return (
          row.capped !== row.initialCapped ||
          (row.capped && row.value !== row.initialValue)
        );
      });

      await Promise.all(
        updates.map((workspace) => {
          const row = rows[workspace.application.id];

          return updateWorkspaceUsageLimits({
            applicationId: workspace.application.id,
            limits: {
              [limit.key]: row.capped
                ? toApiValue(limit, Number(row.value))
                : null,
            } as Partial<OrganizationUsageLimits>,
          });
        })
      );

      onClose();
    } catch (error) {
      setSaveError(getSaveErrorMessage(error));
    }

    setIsSaving(false);
  }

  return (
    <Dialog.Root
      className="w-[37.5rem]"
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog.Title>
        <Heading level="3" asChild>
          <span>Distribute {limit.name}</span>
        </Heading>
      </Dialog.Title>

      <Text>
        {limit.isRetention ? 'Org max' : 'Org limit'}: {organizationLimitLabel}{' '}
        ·{' '}
        {limit.isRetention
          ? 'cap a workspace below the org max or leave it uncapped to inherit it.'
          : 'cap a workspace or leave it uncapped to share the org limit.'}
      </Text>

      {saveError && (
        <FormMessage icon="warning" intent="danger">
          {saveError}
        </FormMessage>
      )}

      <div className="mt-4 flex flex-col gap-4">
        {rowStates.map(({ workspace, row, exceedsOrganizationLimit }) => (
          <AllocationRow
            key={workspace.application.id}
            applicationId={workspace.application.id}
            name={workspace.application.name}
            limit={limit}
            organizationDisplayLimit={organizationDisplayLimit}
            row={row}
            hasError={exceedsOrganizationLimit}
            onModeChange={setRowMode}
            onValueChange={setRowValue}
          />
        ))}

        {workspaces.length > editableWorkspaces.length && (
          <Text size="small">
            Limits of some workspaces are still loading or unavailable. They
            are not shown here.
          </Text>
        )}
      </div>

      <div
        className={twMerge(
          'mt-5 rounded-lg border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-bg))] px-3.5 py-2.5 text-sm text-[hsl(var(--ui-text))]',
          isOvercommitted &&
            'border-[hsl(var(--warning-7))] bg-[hsl(var(--warning-2))] text-[hsl(var(--warning-11))]'
        )}
      >
        {summary}
      </div>

      {hasError && (
        <Text intent="danger" className="mt-2">
          Workspace limits cannot exceed the organization limit of{' '}
          {organizationLimitLabel}.
        </Text>
      )}

      <div className="mt-5">
        <ButtonGroup>
          <Button
            variant="primary"
            loading={isSaving}
            disabled={hasError || isIncomplete}
            onClick={handleSave}
          >
            Save allocation
          </Button>

          <Dialog.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Dialog.Close>
        </ButtonGroup>
      </div>
    </Dialog.Root>
  );
}

// Memoized: slider drags fire many state updates; only the edited row's
// props change, so the other rows (each with a Mantine input) skip
// re-rendering.
const AllocationRow = React.memo(function AllocationRow({
  name,
  applicationId,
  limit,
  organizationDisplayLimit,
  row,
  hasError,
  onModeChange,
  onValueChange,
}: {
  name: string;
  applicationId: string;
  limit: WorkspaceAllocatableLimit;
  organizationDisplayLimit: number | null;
  row: RowState;
  hasError: boolean;
  onModeChange: (applicationId: string, capped: boolean) => void;
  onValueChange: (applicationId: string, value: number | '') => void;
}) {
  const inputUnit = limit.unit === 'filesize' ? 'GiB' : limit.limitUnit;

  function handleInputChange(value: number | string) {
    if (value === '' || value === null || Number.isNaN(Number(value))) {
      onValueChange(applicationId, '');
      return;
    }

    onValueChange(applicationId, Number(value));
  }

  return (
    <div className="grid grid-cols-[minmax(0,8.5rem)_auto_1fr] items-center gap-x-3.5">
      <div
        className="truncate text-sm font-semibold text-[hsl(var(--ui-text-highContrast))]"
        title={name}
      >
        {name}
      </div>

      <div className="flex rounded-lg bg-[hsl(var(--ui-control-bg))] p-[3px]">
        <ModeButton
          active={!row.capped}
          onClick={() => onModeChange(applicationId, false)}
        >
          Uncapped
        </ModeButton>
        <ModeButton
          active={row.capped}
          onClick={() => onModeChange(applicationId, true)}
        >
          Capped
        </ModeButton>
      </div>

      <div className="flex min-w-0 items-center gap-3">
        {organizationDisplayLimit !== null && (
          <input
            type="range"
            min={0}
            max={organizationDisplayLimit}
            step={1}
            value={row.value === '' ? 0 : row.value}
            disabled={!row.capped}
            onChange={(event) => handleInputChange(event.target.value)}
            className="min-w-0 flex-1 accent-[hsl(var(--primary-9))] disabled:opacity-30"
            aria-label={`${name} ${limit.name} limit`}
          />
        )}

        <div className="ml-auto w-[8.5rem] flex-none">
          {row.capped ? (
            <NumberInput
              unit={inputUnit}
              min={0}
              value={row.value}
              onChange={handleInputChange}
              size="small"
              classNames={{
                input: hasError
                  ? 'outline outline-1 outline-[hsl(var(--danger-9))]'
                  : undefined,
              }}
              aria-label={`${name} ${limit.name} limit`}
            />
          ) : (
            <Text
              align="end"
              className="my-0 text-sm text-[hsl(var(--ui-text-placeholder))]"
            >
              Uncapped
            </Text>
          )}
        </div>
      </div>
    </div>
  );
});

function ModeButton({
  active,
  onClick,
  children,
}: React.PropsWithChildren<{ active: boolean; onClick: () => void }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        'rounded-md px-2.5 py-1 text-sm font-medium transition',
        active
          ? 'bg-[hsl(var(--ui-fg))] text-[hsl(var(--primary-11))] shadow-sm'
          : 'text-[hsl(var(--ui-text-lowContrast))] hover:text-[hsl(var(--ui-text))]'
      )}
    >
      {children}
    </button>
  );
}
